import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { Reveal, MaskedLines } from "./Reveal";
import { ShieldCheck, MapPin, Handshake } from "lucide-react";

const HEADSHOT = "https://customer-assets-jai6qajn.emergentagent.net/job_insurance-vr-site/artifacts/0ncqvkyk_Headshot.png";

const CREDS = [
  { icon: ShieldCheck, label: "Licensed & independent" },
  { icon: MapPin, label: "Arizona & select states" },
  { icon: Handshake, label: "Advice that works for you" },
];

export const Founder = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["6%", "-6%"]);

  return (
    <section data-testid="founder-section" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-8">
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Meet Your Broker</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <div ref={ref} className="grid lg:grid-cols-[0.85fr_1.15fr] gap-14 lg:gap-24 items-center">
          {/* portrait */}
          <Reveal className="order-1">
            <div className="relative">
              <div className="absolute -inset-4 rounded-sm border border-[#d4af37]/25" />
              <div className="absolute -top-6 -left-6 mono text-[10px] uppercase tracking-[0.3em] text-[#737373]">Est. Independent Broker</div>
              <div className="relative overflow-hidden rounded-sm bg-gradient-to-b from-[#161616] to-[#0a0a0a]">
                <motion.img
                  style={{ y }}
                  src={HEADSHOT}
                  alt="John Campbell, Independent Life Insurance Broker"
                  className="w-full h-[440px] lg:h-[560px] object-cover object-top"
                />
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(80% 60% at 50% 20%, transparent 40%, rgba(3,3,3,0.55) 100%)" }} />
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
              </div>
              <div className="absolute bottom-5 left-6">
                <div className="serif text-2xl text-[#f4f0ea]">John Campbell</div>
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#d4af37] mt-1">Independent Life Insurance Broker</div>
              </div>
            </div>
          </Reveal>

          {/* intro copy */}
          <div className="order-2">
            <Reveal>
              <h2 className="serif font-light leading-[1.05] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl mb-5">
                <MaskedLines
                  lines={[
                    <>Hi, I'm <span className="italic text-[#d4af37]">John Campbell</span>.</>,
                    <>I protect what you</>,
                    <>love most.</>,
                  ]}
                />
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-[#a3a3a3] text-lg leading-relaxed tracking-wide max-w-xl mb-6">
                An independent life insurance broker based in{" "}
                <Link to="/life-insurance-chandler-az" data-testid="founder-chandler-link" className="text-[#d4af37] hover:text-[#f0d57d] link-underline">Chandler</Link>,
                serving families throughout <span className="text-[#f4f0ea]">Arizona</span> and in select states. Because I'm independent, I answer to you — not a single
                carrier — comparing more than 25 A-rated or better insurance carriers to find coverage that genuinely fits your life and legacy.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="text-[#737373] leading-relaxed tracking-wide max-w-xl mb-10">
                No pressure. No jargon. Just honest guidance and a plan you'll actually understand — reviewed as your family and priorities grow.
              </p>
            </Reveal>

            <Reveal delay={0.24} className="grid sm:grid-cols-3 gap-5 mb-12">
              {CREDS.map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.label} className="flex items-center gap-3 border-t border-white/10 pt-5">
                    <Icon size={18} className="text-[#d4af37] shrink-0" />
                    <span className="text-sm text-[#f4f0ea]/85 tracking-wide">{c.label}</span>
                  </div>
                );
              })}
            </Reveal>

            <Reveal delay={0.3}>
              <a
                href="#quote"
                data-testid="founder-cta"
                className="group inline-flex items-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-4 border border-[#d4af37]/50 text-[#d4af37] rounded-full hover:bg-[#d4af37] hover:text-[#030303] transition-colors duration-400"
              >
                Work with John
                <span className="transition-transform duration-400 group-hover:translate-x-1">→</span>
              </a>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
